import { useMemo, useState, useEffect } from "react";
import { Button } from "@mui/material";
import FileManager, {
  ItemView,
  Permissions,
  Toolbar,
  Item,
} from "devextreme-react/file-manager";
import { fetchWithToken } from "../../../hooks/useFetch";
import CustomFileSystemProvider from "devextreme/file_management/custom_provider";
import { useAuth } from "src/contexts/auth";
import { BusyOverlay } from "src/components/BusyOverlay";
import { SelectBox } from "devextreme-react";
import {
  telemetryInstrumentFolder,
  telemetryLogsRoot,
} from "src/utils/telemetry-logs-path";

type RunLogsProps = {
  tenantID: String;
  instrumentID: String;
  instrumentName: String;
  instrumentSerialNumber: String;
  instrumentModel?: string;
  runID?: String;
  onClose?: () => void;
};

const bucket = "elembio-prod-usw2-s3-aviti-telemetry-logs-usw2";

const fallbackRecipeTypeItems = ["Run", "_Check", "_Prime"];

const RunLogs = ({
  tenantID,
  instrumentID,
  instrumentSerialNumber,
  instrumentModel,
  runID,
  onClose,
}: RunLogsProps) => {
  const { getAccessTokenSilently } = useAuth();
  const [isDownloading, setIsDownloading] = useState(false);
  const [recipeType, setRecipeType] = useState("Run");
  const [recipeTypeItems, setRecipeTypeItems] = useState(fallbackRecipeTypeItems);
  const [currentPath, setCurrentPath] = useState("");
  const [selectedItemPath, setSelectedItemPath] = useState("");

  const tenantPrefix = tenantID === "" ? "ten_traefikee" : tenantID;
  const instrumentFolder = telemetryInstrumentFolder(instrumentID, instrumentSerialNumber);
  const logsRoot = telemetryLogsRoot(instrumentModel);
  const runsPrefix = `${logsRoot}/${tenantPrefix}/${instrumentFolder}/runs/`;

  const prefix = useMemo(() => {
    return `${logsRoot}/${tenantPrefix}/${instrumentFolder}${
      runID ? `/runs/${runID}${recipeType === "Run" ? "" : recipeType}` : ""
    }`;
  }, [logsRoot, tenantPrefix, instrumentFolder, runID, recipeType]);

  useEffect(() => {
    if (!runID) {
      setRecipeTypeItems(fallbackRecipeTypeItems);
      return;
    }

    let cancelled = false;
    fetchWithToken(getAccessTokenSilently, "/run/logRecipeTypes", {
      method: "POST",
      body: JSON.stringify({
        Bucket: bucket,
        Prefix: runsPrefix,
        RunID: runID,
      }),
    })
      .then((items) => {
        if (cancelled) return;
        const list =
          Array.isArray(items) && items.length > 0
            ? items
            : fallbackRecipeTypeItems;
        setRecipeTypeItems(list);
        setRecipeType((current) =>
          list.includes(current) ? current : list[0]
        );
      })
      .catch(() => {
        if (!cancelled) {
          setRecipeTypeItems(fallbackRecipeTypeItems);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [runID, runsPrefix, getAccessTokenSilently]);

  const fileSystemProvider = useMemo(() => {
    return new CustomFileSystemProvider({
      downloadItems: (items) => {
        console.log(items);

        setIsDownloading(true);
        const paths = items.map((v) => v.path.replaceAll("//", "/"));
        const payload = {
          Paths: paths,
          Bucket: bucket,
          Prefix: prefix,
        };
        console.log("payload", payload);
        fetchWithToken(getAccessTokenSilently, "/run/download", {
          method: "POST",
          body: JSON.stringify(payload),
        }).then((blob) => {
          const B = new Blob([blob], { type: "application/octet-stream" });
          const url = window.URL.createObjectURL(B);
          const a = document.createElement("a");
          a.href = url;
          a.setAttribute("download", `archive.zip`);
          document.body.appendChild(a);
          a.click();
          a.parentNode.removeChild(a);
          window.URL.revokeObjectURL(url);
          setIsDownloading(false);
        });
      },
      getItems: (fitem) => {
        console.log(fitem);
        const req = {
          Path: fitem.path?.replaceAll("//", "/"),
          Bucket: bucket,
          Prefix: prefix,
        };
        console.log("request", req);
        return fetchWithToken(getAccessTokenSilently, "/run/logs", {
          method: "POST",
          body: JSON.stringify(req),
        });
      },
      // ...
    });
  }, [getAccessTokenSilently, prefix]);

  // Reset current path when run or recipe type changes
  useEffect(() => {
    setCurrentPath("");
    setSelectedItemPath("");
  }, [runID, recipeType]);

  // Helper function to clean up path formatting
  const formatPath = (path: string) => {
    if (!path) return "";
    // Remove multiple consecutive slashes and ensure single leading slash
    const cleanPath = path.replace(/\/+/g, "/");
    return cleanPath.startsWith("/") ? cleanPath : "/" + cleanPath;
  };

  // Get the display path (either selected item or current directory)
  const displayPath = useMemo(() => {
    const pathToShow = selectedItemPath || currentPath;
    return formatPath(pathToShow);
  }, [selectedItemPath, currentPath]);

  return (
    <div id="parentDivList" style={{ marginLeft: 30, marginRight: "auto" }}>
      <div style={{ display: "flex" }}>
        {onClose && (
          <Button
            sx={{ ml: 1, mr: 2, mb: 1 }}
            variant="contained"
            onClick={() => onClose()}
          >
            Back
          </Button>
        )}
        <div style={{ margin: 8 }}>
          s3://{bucket}/{prefix}
          {displayPath}
        </div>
        {runID && (
          <SelectBox
            items={recipeTypeItems}
            value={recipeType}
            onValueChanged={(e) => {
              setRecipeType(e.value);
            }}
          />
        )}
      </div>
      <FileManager
        key={`${runID}-${recipeType}`}
        height="80vh"
        fileSystemProvider={fileSystemProvider}
        onCurrentDirectoryChanged={(e) => {
          // Update the current path when user navigates to a folder
          const newPath = e.component.option("currentPath") || "";
          setCurrentPath(newPath);
          setSelectedItemPath(""); // Clear selected item when navigating
        }}
        onSelectionChanged={(e) => {
          // Update the selected item path when user selects a file or folder
          if (e.selectedItems && e.selectedItems.length > 0) {
            const selectedItem = e.selectedItems[0];
            setSelectedItemPath(selectedItem.path || "");
          } else {
            setSelectedItemPath("");
          }
        }}
      >
        <ItemView />
        <Permissions
          create={false}
          copy={false}
          move={false}
          delete={false}
          rename={false}
          upload={false}
          download={true}
        ></Permissions>

        <Toolbar>
          <Item name="showNavPane" visible="true" />
          <Item name="refresh" />
        </Toolbar>
      </FileManager>
      {isDownloading && <BusyOverlay />}
    </div>
  );
};

export default RunLogs;
